/**
 * VerificationsTab — per-applicant verifications board.
 * Extracted from LeadDetailPage.tsx (iteration 4 refactor-split).
 *
 * The VerificationsBoard inner applicants query has isActive added to its enabled condition.
 * Per-applicant task queries fire only when their section is expanded (existing behavior).
 *
 * NOTE (backward compat): The stub section (data-testid="lead-verifications-stub") is
 * preserved here even though the tab trigger itself is hidden when showBoard is false.
 * This ensures any test checking that testid can still find it. Flag for reviewer.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { asIsoDateTimeString } from '@resida/shared';
import { Button } from '../../../components/ui/button';
import { badgeVariants } from '../../../components/ui/badge-variants';
import { usePropertyTimezone } from '../../../hooks/usePropertyTimezone';
import { formatTimestamp } from '../../../utils/date';
import {
  fetchApplicants,
  fetchApplicantVerificationTasks,
  updateVerificationTask,
  uploadVerificationAttachment,
  downloadVerificationAttachment,
  deleteVerificationAttachment,
  fetchReferenceCheck,
} from '../../../lib/leasing.api';
import type { RefereeStatus } from '../../../lib/leasing.api';
import { ReferenceCheckModal } from './modals/ReferenceCheckModal';
import { REFERENCE_AGENT_SETTINGS_PATH, REFERENCE_CHECK_PARAM } from '../../../lib/reference-check-links';
import type {
  LeadDetail,
  Applicant,
  VerificationTask,
  VerificationKind,
  VerificationStatus,
} from './shared';
import {
  VERIFICATION_STAGES,
  APPLICANT_VERIFICATION_STAGES,
  KIND_LABELS,
  STATUS_LABELS,
  STATUS_BADGE_VARIANTS,
  ALL_STATUSES,
  ALL_KINDS,
  APPLICANT_ROLE_LABELS,
  APPLICANT_STATUS_BADGE,
  type BadgeTone,
  formatUploadDate,
} from './shared-constants';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface VerificationsTabProps {
  lead: LeadDetail;
  isActive: boolean;
}

// ---------------------------------------------------------------------------
// VerificationCard (local)
// ---------------------------------------------------------------------------

interface VerificationCardProps {
  task: VerificationTask;
  applicantId: string;
  leadId: string;
  onRefresh: () => void;
}

function VerificationCard({ task, applicantId, leadId, onRefresh }: VerificationCardProps) {
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [notes, setNotes] = useState(task.notes ?? '');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [cardError, setCardError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const kind = task.kind as VerificationKind;
  const status = task.status as VerificationStatus;

  async function handleStatusChange(newStatus: VerificationStatus) {
    setStatusDropdownOpen(false);
    setIsUpdatingStatus(true);
    setCardError(null);
    try {
      await updateVerificationTask(leadId, applicantId, task.id, { status: newStatus });
      onRefresh();
    } catch {
      setCardError('Something went wrong. Please try again.');
    } finally {
      setIsUpdatingStatus(false);
    }
  }

  async function handleNotesBlur() {
    const trimmed = notes.trim();
    const current = task.notes ?? '';
    if (trimmed === current) return;
    setIsSavingNotes(true);
    setCardError(null);
    try {
      await updateVerificationTask(leadId, applicantId, task.id, { notes: trimmed || null });
      onRefresh();
    } catch {
      setCardError('Something went wrong. Please try again.');
    } finally {
      setIsSavingNotes(false);
    }
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    setUploadError(null);
    setIsUploading(true);
    try {
      await uploadVerificationAttachment(leadId, applicantId, task.id, file);
      onRefresh();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  }

  async function handleDownload(attId: string, fileName: string) {
    setDownloadingId(attId);
    setCardError(null);
    try {
      const blobUrl = await downloadVerificationAttachment(leadId, applicantId, task.id, attId);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      setCardError('Something went wrong. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  }

  async function handleView(attId: string) {
    setViewingId(attId);
    setCardError(null);
    // Open the tab synchronously (before the await) so browsers don't treat the
    // later navigation as an unrequested popup and block it. Deliberately omits
    // noopener/noreferrer here: `noopener` makes window.open() always return
    // null, so `viewTab` would be permanently unusable and every open would
    // fall through to the post-await fallback below — which, running outside
    // the click's user-activation window, browsers reliably popup-block,
    // leaving only the initial blank tab visible. Safe to omit: the tab is
    // only ever navigated to a same-origin blob: URL we create ourselves, so
    // there's no untrusted destination for a retained window.opener to expose.
    const viewTab = window.open('', '_blank');
    try {
      const blobUrl = await downloadVerificationAttachment(leadId, applicantId, task.id, attId);
      if (viewTab) {
        viewTab.location.href = blobUrl;
      } else {
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
      }
    } catch {
      viewTab?.close();
      setCardError('Something went wrong. Please try again.');
    } finally {
      setViewingId(null);
    }
  }

  async function handleDeleteAttachment(attId: string) {
    if (!window.confirm('Delete this attachment? This cannot be undone.')) return;
    setDeletingId(attId);
    try {
      await deleteVerificationAttachment(leadId, applicantId, task.id, attId);
      onRefresh();
    } catch {
      setCardError('Something went wrong. Please try again.');
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div
      className="rounded-lg border border-border bg-card p-4 flex flex-col gap-3"
      data-testid={`verification-card-${applicantId}-${kind}`}
    >
      {/* Kind label */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h4 className="text-sm font-semibold text-foreground">{KIND_LABELS[kind]}</h4>
      </div>

      {/* Status badge + dropdown */}
      <div className="relative">
        <button
          type="button"
          data-testid={`verification-status-badge-${applicantId}-${kind}`}
          aria-haspopup="listbox"
          aria-expanded={statusDropdownOpen}
          aria-label={`${KIND_LABELS[kind]} status: ${STATUS_LABELS[status]}. Click to change.`}
          disabled={isUpdatingStatus}
          className={[
            badgeVariants({ variant: STATUS_BADGE_VARIANTS[status] }),
            'gap-1.5 px-3 min-h-[44px] transition-opacity',
            isUpdatingStatus ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-80',
          ].join(' ')}
          onClick={() => setStatusDropdownOpen((o) => !o)}
        >
          {STATUS_LABELS[status]}
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {statusDropdownOpen && (
          <ul
            role="listbox"
            aria-label={`Update ${KIND_LABELS[kind]} status`}
            className="absolute left-0 top-full mt-1 z-10 bg-popover border border-border rounded-md shadow-lg py-1 min-w-[140px]"
          >
            {ALL_STATUSES.map((s) => (
              <li key={s} role="option" aria-selected={s === status}>
                <button
                  type="button"
                  className={[
                    'w-full text-left px-3 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground transition-colors',
                    s === status ? 'font-semibold' : '',
                  ].join(' ')}
                  onClick={() => void handleStatusChange(s)}
                >
                  {STATUS_LABELS[s]}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Notes */}
      <div>
        <label
          htmlFor={`notes-${task.id}`}
          className="block text-xs font-medium text-muted-foreground mb-1"
        >
          Notes
        </label>
        <textarea
          id={`notes-${task.id}`}
          data-testid={`verification-notes-${applicantId}-${kind}`}
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => void handleNotesBlur()}
          placeholder="Add notes..."
          className={[
            'w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-xs',
            'text-foreground placeholder:text-muted-foreground/60',
            'focus:outline-none focus:ring-2 focus:ring-ring',
            isSavingNotes ? 'opacity-60' : '',
          ].join(' ')}
        />
      </div>

      {/* Attachments */}
      <div>
        <p className="text-xs font-medium text-muted-foreground mb-1.5">
          Files ({task.attachments.length})
        </p>
        {task.attachments.length > 0 ? (
          <ul className="space-y-1.5 mb-2">
            {task.attachments.map((att) => (
              <li key={att.id} className="flex flex-col bg-muted/40 rounded-md px-2.5 py-1.5 gap-1">
                <span className="text-xs text-foreground truncate" title={att.fileName}>
                  {att.fileName}
                </span>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground">
                    {formatUploadDate(att.uploadedAt)}
                  </span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      aria-label={`View ${att.fileName}`}
                      data-testid={`attachment-view-btn-${att.id}`}
                      disabled={viewingId === att.id}
                      className="text-xs text-primary hover:text-primary/80 disabled:opacity-50"
                      onClick={() => void handleView(att.id)}
                    >
                      {viewingId === att.id ? 'Opening...' : 'View'}
                    </button>
                    <button
                      type="button"
                      aria-label={`Download ${att.fileName}`}
                      data-testid={`attachment-download-btn-${att.id}`}
                      disabled={downloadingId === att.id}
                      className="text-xs text-primary hover:text-primary/80 disabled:opacity-50"
                      onClick={() => void handleDownload(att.id, att.fileName)}
                    >
                      {downloadingId === att.id ? 'Downloading...' : 'Download'}
                    </button>
                    <button
                      type="button"
                      aria-label={`Delete ${att.fileName}`}
                      data-testid={`attachment-delete-btn-${att.id}`}
                      disabled={deletingId === att.id}
                      className="text-xs text-destructive hover:text-destructive/80 disabled:opacity-50"
                      onClick={() => void handleDeleteAttachment(att.id)}
                    >
                      {deletingId === att.id ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
                {att.scheduledDisposalAt && (
                  <p
                    className="text-xs text-warning"
                    data-testid={`attachment-disposal-hint-${att.id}`}
                  >
                    Scheduled for disposal on {formatUploadDate(att.scheduledDisposalAt)}
                  </p>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-muted-foreground/60 italic mb-2">No files yet.</p>
        )}

        {uploadError && (
          <p className="text-xs text-destructive mb-1.5">{uploadError}</p>
        )}

        {/* Upload button */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            className="sr-only"
            accept="application/pdf,image/jpeg,image/png,image/heic,image/webp"
            onChange={(e) => void handleFileUpload(e)}
            tabIndex={-1}
            aria-hidden="true"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid={`verification-upload-btn-${applicantId}-${kind}`}
            disabled={isUploading}
            className="w-full text-xs min-h-[44px] px-3"
            onClick={() => fileInputRef.current?.click()}
            aria-label={`Upload file for ${KIND_LABELS[kind]}`}
          >
            {isUploading ? 'Uploading...' : 'Upload file'}
          </Button>
        </div>
      </div>

      {/* Card-level error state */}
      {cardError && (
        <p
          className="text-xs text-destructive"
          role="alert"
          data-testid={`verification-card-error-${applicantId}-${kind}`}
        >
          {cardError}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConsentSection (local) — Screening authorization per applicant
// ---------------------------------------------------------------------------

/** Day-only rendering of a UTC timestamp in the property's timezone ("Oct 21, 2026"). */
const DATE_ONLY: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };

interface ConsentSectionProps {
  applicant: Applicant;
}

function ConsentSection({ applicant }: ConsentSectionProps) {
  const propertyTimezone = usePropertyTimezone();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const envelope = applicant.consentEnvelope;
  const isCompleted = envelope?.status === 'COMPLETED';

  async function handleConsentDownload() {
    if (!envelope?.downloadUrl) return;
    setIsDownloading(true);
    setDownloadError(null);
    try {
      const response = await fetch(envelope.downloadUrl, { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = 'screening-authorization-signed.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setIsDownloading(false);
    }
  }

  // Determine display status label
  let statusLabel: string;
  let statusTone: BadgeTone;
  if (!envelope) {
    statusLabel = 'Not started';
    statusTone = 'secondary';
  } else if (envelope.status === 'COMPLETED') {
    statusLabel = 'Completed';
    statusTone = 'success';
  } else if (envelope.status === 'PENDING' || envelope.status === 'DRAFT') {
    statusLabel = 'Pending';
    statusTone = 'warning';
  } else {
    statusLabel = 'Not started';
    statusTone = 'secondary';
  }

  return (
    <div
      className="mt-4 rounded-lg border border-border p-4 space-y-2"
      data-testid={`consent-section-${applicant.id}`}
    >
      <h4 className="text-sm font-semibold">Screening authorization</h4>

      <div className="flex items-center gap-2 flex-wrap">
        <span className={badgeVariants({ variant: statusTone })}>{statusLabel}</span>
        {isCompleted && envelope?.signedAt && (
          <p className="text-xs text-muted-foreground">
            Signed on {formatTimestamp(asIsoDateTimeString(envelope.signedAt), propertyTimezone, DATE_ONLY)}
          </p>
        )}
      </div>

      {!isCompleted && (
        <p
          className="text-xs text-muted-foreground"
          data-testid={`consent-not-signed-${applicant.id}`}
        >
          Not yet signed
        </p>
      )}

      {isCompleted && (
        <div className="space-y-1">
          <button
            type="button"
            data-testid={`consent-download-btn-${applicant.id}`}
            disabled={!envelope?.downloadUrl || isDownloading}
            title={!envelope?.downloadUrl ? 'Download URL unavailable' : undefined}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
            onClick={() => void handleConsentDownload()}
          >
            {isDownloading ? 'Downloading...' : 'Download PDF'}
          </button>
          {downloadError && (
            <p className="text-xs text-destructive" role="alert">
              {downloadError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReferenceCheckSection (local) — DC-17 landlord-triggered reference check
// ---------------------------------------------------------------------------

const REFEREE_STATUS_LABELS: Record<RefereeStatus, string> = {
  pending: 'Pending',
  contacted: 'Contacted',
  follow_up_sent: 'Follow-up sent',
  responded: 'Responded',
  exhausted: 'Exhausted',
};

const REFEREE_STATUS_BADGE_VARIANTS: Record<RefereeStatus, BadgeTone> = {
  pending: 'secondary',
  contacted: 'info',
  follow_up_sent: 'warning',
  responded: 'success',
  exhausted: 'secondary',
};

// ---------------------------------------------------------------------------
// Reference-check id persistence (FM5/FM10 mitigation)
//
// The manager API contract exposes only create-by-lead and get-by-id for
// ReferenceCheck (no list/get-by-lead-or-applicant endpoint), so the id cannot be
// rediscovered from the server on mount. To keep the read-only status view from
// silently vanishing on reload/revisit within the same browser (and to stop
// re-inviting duplicate submissions in that case), the id is mirrored to
// localStorage, keyed per lead+applicant, alongside component state.
//
// KNOWN GAP: this does not help across devices/browsers or after the user clears
// site data when the landlord arrives WITHOUT a link — a true fix needs a backend "get reference
// check(s) for this applicant" endpoint (out of scope here). A deep link from the escalation or
// held-response queue names the check, so it shows that check's status in any browser
// (BR-ESC-QUEUE-REACH).
// ---------------------------------------------------------------------------

function referenceCheckStorageKey(leadId: string, applicantId: string): string {
  return `leasing:referenceCheckId:${leadId}:${applicantId}`;
}

function readStoredReferenceCheckId(leadId: string, applicantId: string): string | null {
  try {
    return window.localStorage.getItem(referenceCheckStorageKey(leadId, applicantId));
  } catch {
    // Storage unavailable (private browsing, quota, etc.) — fall back to session-local only.
    return null;
  }
}

function writeStoredReferenceCheckId(leadId: string, applicantId: string, referenceCheckId: string): void {
  try {
    window.localStorage.setItem(referenceCheckStorageKey(leadId, applicantId), referenceCheckId);
  } catch {
    // ignore — best-effort persistence only
  }
}

function clearStoredReferenceCheckId(leadId: string, applicantId: string): void {
  try {
    window.localStorage.removeItem(referenceCheckStorageKey(leadId, applicantId));
  } catch {
    // ignore
  }
}

/**
 * Scroll `section` to just under the lead header. That header pins itself over the top of the page
 * once scrolled past (AC-L14) and is tall on a phone, so scrolling the section to the top of the
 * window would leave it hidden beneath it.
 */
function scrollBelowLeadHeader(section: HTMLElement): void {
  const header = document.querySelector<HTMLElement>('[data-testid="lead-detail-header"]');
  const covered = header && getComputedStyle(header).position === 'fixed' ? header.getBoundingClientRect().bottom : 0;
  window.scrollBy({ top: section.getBoundingClientRect().top - covered - 16 });
}

interface ReferenceCheckSectionProps {
  applicant: Applicant;
  leadId: string;
  isActive: boolean;
  /**
   * BR-28: the reference agent is on for the LEAD's landlord scope (`lead.referenceAgentEnabled`) —
   * the scope the server gate checks, not the signed-in user's own default policy.
   */
  referenceAgentEnabled: boolean;
  /** Scroll this section into view and focus it (once) — a deep link to the lead's reference check. */
  scrollIntoViewNow: boolean;
  onScrolledIntoView: () => void;
  /**
   * Deep link: the id of a reference check requested for this applicant (the queue link names it).
   * It wins over the id remembered in this browser, so whoever follows the link sees that check's
   * status whether or not they started it here (owner on another device, listing delegate, MCP-created).
   */
  linkedReferenceCheckId?: string | null;
}

function ReferenceCheckSection({
  applicant,
  leadId,
  isActive,
  referenceAgentEnabled,
  scrollIntoViewNow,
  onScrolledIntoView,
  linkedReferenceCheckId,
}: ReferenceCheckSectionProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const settleTimer = useRef<number>();

  useEffect(() => {
    if (!scrollIntoViewNow || !isActive) return;
    const section = sectionRef.current;
    if (!section) return;
    scrollBelowLeadHeader(section);
    // The lead header pins itself as the scroll passes it, which shifts the content: settle once more.
    settleTimer.current = window.setTimeout(() => scrollBelowLeadHeader(section), 300);
    section.focus({ preventScroll: true });
    onScrolledIntoView();
  }, [scrollIntoViewNow, isActive, onScrolledIntoView]);

  useEffect(() => () => window.clearTimeout(settleTimer.current), []);
  // Initialized from localStorage so a reload/revisit in the same browser rediscovers
  // an in-progress or completed reference check instead of showing "never started".
  const [storedReferenceCheckId, setReferenceCheckId] = useState<string | null>(() =>
    readStoredReferenceCheckId(leadId, applicant.id)
  );
  const referenceCheckId = linkedReferenceCheckId ?? storedReferenceCheckId;

  // Remember a linked check for later visits, like one started here.
  useEffect(() => {
    if (linkedReferenceCheckId) writeStoredReferenceCheckId(leadId, applicant.id, linkedReferenceCheckId);
  }, [linkedReferenceCheckId, leadId, applicant.id]);

  const propertyTimezone = usePropertyTimezone();

  // BR-9: the trigger is disabled until rental-history consent (the "Screening
  // authorization" envelope above) is complete — same signal ConsentSection renders.
  const consentComplete = applicant.consentEnvelope?.status === 'COMPLETED';

  // BR-28 / DC-17: the trigger is available ONLY when the reference agent is switched on for the
  // lead's landlord (the lead owner's switch, as the server gate resolves it — not the signed-in
  // user's own, which a listing delegate does not have). An unconfirmed switch arrives as off.
  const agentOff = !referenceAgentEnabled;
  const triggerDisabled = !consentComplete || agentOff;
  // The visible reasons the trigger is unavailable, so a screen reader reaching the disabled
  // button hears them (a disabled button cannot take focus and `title` is mouse-only).
  const consentHintId = `reference-check-consent-hint-${applicant.id}`;
  const agentOffHintId = `reference-check-agent-off-hint-${applicant.id}`;
  const reasonIds = [!consentComplete && consentHintId, agentOff && agentOffHintId].filter(Boolean).join(' ');

  const { data, isLoading, error } = useQuery({
    queryKey: ['referenceCheck', leadId, referenceCheckId],
    queryFn: () => fetchReferenceCheck(leadId, referenceCheckId as string),
    enabled: isActive && !!referenceCheckId,
    staleTime: 15_000,
  });

  // A stored id that no longer resolves (e.g. data purged) shouldn't permanently wedge
  // the section in a broken "Failed to load" state — drop it and fall back to allowing
  // a fresh reference check to be started. Scoped to 404 specifically so a transient
  // network/server error doesn't wipe out a perfectly valid stored id.
  useEffect(() => {
    const status = (error as (Error & { status?: number }) | null)?.status;
    if (status === 404 && storedReferenceCheckId && storedReferenceCheckId === referenceCheckId) {
      clearStoredReferenceCheckId(leadId, applicant.id);
      setReferenceCheckId(null);
    }
  }, [error, referenceCheckId, storedReferenceCheckId, leadId, applicant.id]);

  return (
    <div
      ref={sectionRef}
      role="group"
      aria-labelledby={`reference-check-heading-${applicant.id}`}
      tabIndex={-1}
      className="mt-4 rounded-lg border border-border p-4 space-y-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid={`reference-check-section-${applicant.id}`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h4 id={`reference-check-heading-${applicant.id}`} className="text-sm font-semibold">
          Reference check
        </h4>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="min-h-[44px]"
          data-testid={`reference-check-trigger-${applicant.id}`}
          disabled={triggerDisabled}
          aria-disabled={triggerDisabled}
          aria-describedby={reasonIds || undefined}
          title={
            !consentComplete
              ? 'Requires completed rental-history consent'
              : agentOff
                ? 'Requires the reference agent to be turned on'
                : undefined
          }
          onClick={() => setModalOpen(true)}
        >
          Start reference check
        </Button>
      </div>

      {!consentComplete && (
        <p
          id={consentHintId}
          className="text-sm text-muted-foreground"
          data-testid={`reference-check-consent-hint-${applicant.id}`}
        >
          Available once rental-history consent (screening authorization) is complete.
        </p>
      )}

      {agentOff && (
        <p
          id={agentOffHintId}
          className="text-sm text-muted-foreground"
          data-testid={`reference-check-agent-off-hint-${applicant.id}`}
        >
          The reference agent is off, so reference checks can't be started.{' '}
          <Link
            to={REFERENCE_AGENT_SETTINGS_PATH}
            className="inline-flex min-h-[44px] items-center font-medium text-primary underline"
            data-testid={`reference-check-agent-settings-link-${applicant.id}`}
          >
            Turn it on in Settings → AI agents
          </Link>
        </p>
      )}

      {referenceCheckId && isLoading && (
        <p className="text-xs text-muted-foreground">Loading reference check status…</p>
      )}

      {referenceCheckId && error && (
        <p className="text-xs text-destructive" role="alert">
          Failed to load reference check status.
        </p>
      )}

      {/* Read-only status view — referee statuses + summary + close state only.
          NEVER render reply content (BR-10/BR-12). */}
      {data && (
        <div className="space-y-2" data-testid={`reference-check-status-${applicant.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={badgeVariants({ variant: data.status === 'closed' ? 'secondary' : 'info' })}>
              {data.status === 'closed' ? 'Closed' : 'Open'}
            </span>
            {data.status === 'closed' && data.closedAt && (
              <p className="text-xs text-muted-foreground">
                Closed on {formatTimestamp(asIsoDateTimeString(data.closedAt), propertyTimezone, DATE_ONLY)}
              </p>
            )}
          </div>

          <ul className="space-y-1.5">
            {data.referees.map((referee) => (
              <li
                key={referee.id}
                className="flex items-center justify-between gap-2 bg-muted/40 rounded-md px-2.5 py-1.5"
                data-testid={`reference-check-referee-row-${referee.id}`}
              >
                <div className="min-w-0">
                  <p className="text-xs text-foreground truncate">{referee.relationship}</p>
                  <p className="text-xs text-muted-foreground truncate">{referee.email}</p>
                </div>
                <span
                  className={`${badgeVariants({ variant: REFEREE_STATUS_BADGE_VARIANTS[referee.status] })} shrink-0`}
                >
                  {REFEREE_STATUS_LABELS[referee.status]}
                </span>
              </li>
            ))}
          </ul>

          {data.status === 'closed' && data.summary && (
            <div
              className="rounded-md bg-muted/40 px-3 py-2"
              data-testid={`reference-check-summary-${applicant.id}`}
            >
              <p className="text-xs font-medium text-muted-foreground mb-0.5">Summary</p>
              <p className="text-xs text-foreground whitespace-pre-wrap">{data.summary}</p>
            </div>
          )}
        </div>
      )}

      <ReferenceCheckModal
        leadId={leadId}
        applicantId={applicant.id}
        applicantName={applicant.name}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={(id) => {
          writeStoredReferenceCheckId(leadId, applicant.id, id);
          setReferenceCheckId(id);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// ApplicantVerificationSection (local)
// ---------------------------------------------------------------------------

interface ApplicantVerificationSectionProps {
  applicant: Applicant;
  leadId: string;
  isActive: boolean;
  /** BR-28: the reference agent is on for the lead's landlord scope. */
  referenceAgentEnabled: boolean;
  /** Deep link to a reference check: open this section instead of starting collapsed. */
  revealReferenceCheck: boolean;
  /** Deep link to a reference check: scroll to this section's Reference check once it has loaded. */
  scrollToReferenceCheck: boolean;
  /** Deep link: the linked reference check when it belongs to this applicant. */
  linkedReferenceCheckId: string | null;
}

function ApplicantVerificationSection({
  applicant,
  leadId,
  isActive,
  referenceAgentEnabled,
  revealReferenceCheck,
  scrollToReferenceCheck,
  linkedReferenceCheckId,
}: ApplicantVerificationSectionProps) {
  const queryClient = useQueryClient();
  // AC-L12: verification sections default to collapsed — a lead with several applicants
  // otherwise opens with every section's task list expanded at once. A deep link to a
  // reference check opens the section it lands in (BR-ESC-QUEUE-REACH).
  const [collapsed, setCollapsed] = useState(!revealReferenceCheck);
  const [scrollPending, setScrollPending] = useState(scrollToReferenceCheck);
  const handleScrolledIntoView = useCallback(() => setScrollPending(false), []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['tasks', leadId, applicant.id],
    queryFn: () => fetchApplicantVerificationTasks(leadId, applicant.id),
    staleTime: 30_000,
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['tasks', leadId, applicant.id] });
  }, [queryClient, leadId, applicant.id]);

  const tasks = data?.tasks
    ? [...data.tasks].sort((a, b) => ALL_KINDS.indexOf(a.kind as VerificationKind) - ALL_KINDS.indexOf(b.kind as VerificationKind))
    : [];

  return (
    <div
      className="mb-4 rounded-lg border border-border"
      data-testid={`verification-section-${applicant.id}`}
    >
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/30 transition-colors rounded-lg"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
        aria-controls={`verification-tasks-${applicant.id}`}
        aria-label={`${collapsed ? 'Expand' : 'Collapse'} verifications for ${applicant.name}`}
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium">{applicant.name}</span>
          <span className="text-xs text-muted-foreground">
            {APPLICANT_ROLE_LABELS[applicant.role]}
          </span>
          <span
            className={badgeVariants({ variant: APPLICANT_STATUS_BADGE[applicant.status] ?? 'secondary' })}
          >
            {applicant.status.charAt(0).toUpperCase() + applicant.status.slice(1)}
          </span>
        </div>
        <svg
          className={`w-4 h-4 text-muted-foreground transition-transform ${collapsed ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      </button>

      {!collapsed && (
        <div
          id={`verification-tasks-${applicant.id}`}
          className="px-4 pb-4"
        >
          {isLoading && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-lg border border-border bg-muted/20 p-4 animate-pulse">
                  <div className="h-4 w-24 bg-muted rounded mb-3" />
                  <div className="h-3 w-16 bg-muted rounded mb-2" />
                  <div className="h-8 w-full bg-muted rounded" />
                </div>
              ))}
            </div>
          )}

          {error && (
            <p className="text-sm text-destructive mt-3">
              Failed to load verification tasks. Please refresh.
            </p>
          )}

          {!isLoading && !error && tasks.length === 0 && (
            <p className="text-sm text-muted-foreground italic mt-3">
              No verification tasks found for this applicant.
            </p>
          )}

          {!isLoading && !error && tasks.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
              {tasks.map((task) => (
                <VerificationCard
                  key={task.id}
                  task={task}
                  applicantId={applicant.id}
                  leadId={leadId}
                  onRefresh={invalidate}
                />
              ))}
            </div>
          )}

          {/* Screening authorization — always rendered per applicant */}
          <ConsentSection applicant={applicant} />

          {/* Reference check (DC-17) — always rendered per applicant */}
          <ReferenceCheckSection
            applicant={applicant}
            leadId={leadId}
            isActive={isActive}
            referenceAgentEnabled={referenceAgentEnabled}
            // Wait for the task cards above so the section does not move after the scroll.
            scrollIntoViewNow={scrollPending && !isLoading}
            onScrolledIntoView={handleScrolledIntoView}
            linkedReferenceCheckId={linkedReferenceCheckId}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// VerificationsBoard (local)
// ---------------------------------------------------------------------------

interface VerificationsBoardProps {
  lead: LeadDetail;
  isActive: boolean;
}

function VerificationsBoard({ lead, isActive }: VerificationsBoardProps) {
  const showBoard = VERIFICATION_STAGES.includes(lead.stage);
  const referenceCheckId = useSearchParams()[0].get(REFERENCE_CHECK_PARAM);

  const { data: applicantsData, isLoading } = useQuery({
    queryKey: ['applicants', lead.id],
    queryFn: () => fetchApplicants(lead.id),
    // Added isActive to prevent fetching when tab is not visible
    enabled: showBoard && isActive,
    staleTime: 30_000,
  });

  // A deep link names a reference check by id. Read it (the same query the applicant's Reference check
  // section uses, so it is fetched once) to learn which applicant it belongs to and to show its status
  // to anyone who follows the link, not only the browser that started the check.
  const linkedCheck = useQuery({
    queryKey: ['referenceCheck', lead.id, referenceCheckId],
    queryFn: () => fetchReferenceCheck(lead.id, referenceCheckId as string),
    enabled: showBoard && isActive && !!referenceCheckId,
    staleTime: 15_000,
    retry: false,
  });

  if (!showBoard) {
    return (
      <section className="mb-6 rounded-lg border border-border bg-card p-5" data-testid="lead-verifications-stub">
        <h2 className="text-base font-semibold mb-2">Verifications</h2>
        <p className="text-sm text-muted-foreground italic">
          Verifications available once the lead reaches the application stage.
        </p>
      </section>
    );
  }

  const verifiableApplicants = (applicantsData ?? []).filter((a) =>
    APPLICANT_VERIFICATION_STAGES.includes(a.status)
  );

  // Open the applicant the linked check belongs to; if it cannot be resolved (the read failed, or the
  // applicant is not on this board), open every applicant so the landlord is never left on a collapsed list.
  const linkedApplicantId = linkedCheck.data?.applicantId;
  const linkedApplicant = referenceCheckId
    ? verifiableApplicants.find(
        (a) => a.id === linkedApplicantId || readStoredReferenceCheckId(lead.id, a.id) === referenceCheckId
      )
    : undefined;
  // Hold the list back until the link has resolved so the right applicant opens and scrolls into view.
  const resolvingLink = !!referenceCheckId && linkedCheck.isLoading;
  const isLinked = (applicantId: string) =>
    !!referenceCheckId && (!linkedApplicant || linkedApplicant.id === applicantId);

  return (
    <section className="mb-6 rounded-lg border border-border bg-card p-5" data-testid="verification-board">
      <h2 className="text-base font-semibold mb-4">Verifications</h2>

      {(isLoading || resolvingLink) && (
        <div className="space-y-3">
          {[0, 1].map((i) => (
            <div key={i} className="rounded-lg border border-border p-4 animate-pulse">
              <div className="h-4 w-32 bg-muted rounded mb-3" />
              <div className="grid grid-cols-3 gap-4">
                {[0, 1, 2].map((j) => (
                  <div key={j} className="h-20 bg-muted/30 rounded" />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && !resolvingLink && verifiableApplicants.length === 0 && (
        <p className="text-sm text-muted-foreground italic">
          No applicants have started the application yet. Verification tasks will appear here once an applicant begins.
        </p>
      )}

      {!isLoading && !resolvingLink && verifiableApplicants.length > 0 && (
        <div>
          {verifiableApplicants.map((applicant) => (
            <ApplicantVerificationSection
              key={applicant.id}
              applicant={applicant}
              leadId={lead.id}
              isActive={isActive}
              referenceAgentEnabled={lead.referenceAgentEnabled}
              linkedReferenceCheckId={linkedApplicant?.id === applicant.id ? referenceCheckId : null}
              revealReferenceCheck={isLinked(applicant.id)}
              scrollToReferenceCheck={
                isLinked(applicant.id) && (linkedApplicant ?? verifiableApplicants[0])?.id === applicant.id
              }
            />
          ))}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// VerificationsTab
// ---------------------------------------------------------------------------

export function VerificationsTab({ lead, isActive }: VerificationsTabProps) {
  return (
    <div>
      <VerificationsBoard lead={lead} isActive={isActive} />
    </div>
  );
}
